<template>
  <div>
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
      <input
        v-model="searchTerm"
        class="form-control"
        style="max-width: 250px"
        placeholder="Search accounts..."
      />

      <div class="dropdown">
        <button class="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
          <i class="bi bi-gear"></i> Actions
        </button>
        <ul class="dropdown-menu dropdown-menu-end">
          <li>
            <a class="dropdown-item" href="#" @click.prevent="fetchGroups">
              <i class="bi bi-arrow-repeat me-2"></i> Refresh
            </a>
          </li>
          <li>
            <a class="dropdown-item" href="#" @click.prevent="exportCSV">
              <i class="bi bi-download me-2"></i> Export CSV
            </a>
          </li>
          <li>
            <button
              class="dropdown-item"
              :disabled="!hasEdits"
              @click="saveAllEdits"
            >
              <i class="bi bi-upload me-2"></i> Update
            </button>
          </li>
        </ul>
      </div>
    </div>

    <!-- Loading / Error -->
    <div v-if="loading" class="text-center my-4">
      <b-spinner />
    </div>
    <b-alert v-if="error" variant="danger" dismissible>
      {{ error }}
    </b-alert>

    <!-- Category Buttons -->
    <div class="mb-3 d-flex flex-wrap gap-2">
      <button
        v-for="(accounts, category) in groupedGroups"
        :key="category"
        class="btn"
        :class="selectedCategory === category ? 'btn-primary' : 'btn-outline-primary'"
        @click="selectedCategory = category"
      >
        {{ category }} ({{ accounts.length }})
      </button>
    </div>

    <!-- TABLE -->
    <div v-if="selectedCategory">
      <div class="bg-primary text-white p-2 fw-bold">
        {{ selectedCategory }}
      </div>

      <!-- Scroll wrapper -->
      <div class="table-scroll-wrapper">
        <b-table
          :items="paginatedAccounts"
          :fields="fields"
          small
          hover
          striped
          responsive
        >
          <template #cell(name)="d">
            <input
              v-if="isEditing(d.item)"
              v-model="editedGroups[d.item.id].name"
              class="form-control form-control-sm"
            />
            <span v-else>{{ d.item.name }}</span>
          </template>

          <template #cell(code)="d">
            <input
              v-if="isEditing(d.item)"
              v-model="editedGroups[d.item.id].code"
              class="form-control form-control-sm"
            />
            <span v-else>{{ d.item.code }}</span>
          </template>

          <template #cell(type)="d">
            <input
              v-if="isEditing(d.item)"
              v-model="editedGroups[d.item.id].type"
              class="form-control form-control-sm"
            />
            <span v-else>{{ d.item.type }}</span>
          </template>

          <template #cell(balance)="d">
            <a href="#" @click.prevent="viewLedger(d.item)">
              {{ formatCurrency(d.item.balance) }}
            </a>
          </template>

          <template #cell(actions)="d">
            <b-button
              size="sm"
              variant="warning"
              v-if="!isEditing(d.item)"
              @click="enableEdit(d.item)"
            >
              <i class="bi bi-pencil"></i>
            </b-button>

            <b-button
              size="sm"
              variant="secondary"
              v-else
              @click="cancelEdit(d.item.id)"
            >
              <i class="bi bi-x"></i>
            </b-button>
          </template>
        </b-table>
      </div>

      <!-- Pagination Controls -->
      <div class="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-2">
          <small>Rows:</small>
          <select
            v-model="perPage"
            class="form-select form-select-sm"
            style="width: 90px"
          >
            <option v-for="n in perPageOptions" :key="n" :value="n">
              {{ n }}
            </option>
          </select>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
          pills
        />
      </div>

      <div class="text-end fw-bold mt-2">
        Subtotal:
        {{ formatCurrency(getSubtotal(groupedGroups[selectedCategory])) }}
      </div>
    </div>
  </div>
</template>

<script>
import accountApi from '@/api/accountApi';
import swal from '@/utils/swal';

export default {
  data() {
    return {
      groups: [],
      editedGroups: {},
      editingRows: {},
      loading: false,
      error: '',
      searchTerm: '',
      selectedCategory: null,

      // pagination
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],

      fields: [
        { key: 'name', sortable: true },
        { key: 'code', sortable: true },
        { key: 'type', sortable: true },
        { key: 'accountCategory' },
        { key: 'balance', sortable: true },
        // { key: 'actions', label: 'Actions' }
      ]
    };
  },

  computed: {
    filteredGroups() {
      const t = this.searchTerm.toLowerCase();
      return this.groups.filter(g =>
        Object.values(g).some(v =>
          String(v ?? '').toLowerCase().includes(t)
        )
      );
    },

    groupedGroups() {
      const map = {};
      this.filteredGroups.forEach(a => {
        const k = a.type || 'Uncategorized';
        if (!map[k]) map[k] = [];
        map[k].push(a);
      });
      return map;
    },

    paginatedAccounts() {
      if (!this.selectedCategory) return [];
      const list = this.groupedGroups[this.selectedCategory] || [];
      const start = (this.currentPage - 1) * this.perPage;
      return list.slice(start, start + this.perPage);
    },

    totalRows() {
      return this.selectedCategory
        ? this.groupedGroups[this.selectedCategory]?.length || 0
        : 0;
    },

    hasEdits() {
      return Object.keys(this.editedGroups).length > 0;
    }
  },

  watch: {
    selectedCategory() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    }
  },

  methods: {
    fetchGroups() {
      this.loading = true;
      accountApi.getAll()
        .then(r => {
          this.groups = r.data;
          this.selectedCategory = Object.keys(this.groupedGroups)[0] || null;
        })
        .catch(() => this.error = 'Failed to load data')
        .finally(() => this.loading = false);
    },

    formatCurrency(v) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES'
      }).format(v || 0);
    },

    getSubtotal(list) {
      return list.reduce((s, a) => s + (a.balance || 0), 0);
    },

    isEditing(i) {
      return !!this.editingRows[i.id];
    },

    enableEdit(i) {
      this.$set(this.editingRows, i.id, true);
      this.$set(this.editedGroups, i.id, { ...i });
    },

    cancelEdit(id) {
      this.$delete(this.editingRows, id);
      this.$delete(this.editedGroups, id);
    },

    async saveAllEdits() {
      await accountApi.batchUpdate(Object.values(this.editedGroups));
      swal.success('Updated successfully');
      this.fetchGroups();
      this.editedGroups = {};
      this.editingRows = {};
    },

    exportCSV() {
      const headers = ['Name', 'Code', 'Type', 'Account Category', 'Balance'];
      const rows = this.filteredGroups.map(group => [
        group.name || '',
        group.code || '',
        group.type || '',
        group.accountCategory || '',
        this.formatCurrency(group.balance)
      ]);
      const csvContent = [headers, ...rows].map(row => row.map(val => `"${val}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const today = new Date().toISOString().split('T')[0];
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `accounts_export_${today}.csv`;
      link.click();
    },

    viewLedger(acc) {
      this.$router.push({ name: 'LedgerSummary', params: { accountId: acc.id } });
    }
  },

  mounted() {
    this.fetchGroups();
  }
};
</script>

<style scoped>
.table-scroll-wrapper {
  max-height: 420px;
  overflow-y: auto;
}
</style>
